import { ref, computed } from 'vue'
import { useAlert } from '@/libs/alertPortal'
import {
  useSettingsPageTeamManagementDetailViewQuery,
  useUpdateTeamMutation,
} from '../SettingsPageTeamManagementDetailView.generated'

export type Staff = {
  guid: string
  displayName: string
  checked?: boolean
}

export type Team = {
  guid: string
  name: string
  staffs: Staff[]
}

export const useTeamDetail = (teamGUID: string) => {
  const { result, loading, refetch } =
    useSettingsPageTeamManagementDetailViewQuery(
      {
        guid: teamGUID,
      },
      {
        fetchPolicy: 'no-cache',
      }
    )

  const alert = useAlert()
  const isSubmitting = ref(false)
  const isLoading = computed(() => loading.value)

  const team = computed<Team | undefined>(() => {
    if (!result.value?.team) return undefined

    return {
      ...result.value.team,
      staffs: result.value.team.staffs.map(staff => ({
        ...staff,
        checked: false,
      })),
    }
  })

  const { mutate: updateTeam } = useUpdateTeamMutation()

  // 更新後すぐにrefetchすると、更新が反映されないことがあるため、500ms遅延させる
  const delayedRefetch = () => {
    setTimeout(() => {
      refetch()
      isSubmitting.value = false
    }, 500)
  }

  const handleUpdateTeam = async (team: Team) => {
    if (isSubmitting.value) {
      return
    }

    if (!team) {
      alert.error('チームの情報が取得できませんでした')
      return
    }

    if (!window.confirm('チームを更新しますか？')) {
      return
    }

    isSubmitting.value = true
    try {
      await updateTeam({
        input: {
          guid: team.guid,
          name: team.name,
          staffGUIDs: team.staffs.map(staff => staff.guid),
        },
      })
      alert.success('チームを更新しました')
    } catch (e) {
      alert.error('更新に失敗しました、もう一度お試しください')
      console.error(e)
    } finally {
      delayedRefetch()
    }
  }

  return {
    team,
    isLoading,
    isSubmitting,
    handleUpdateTeam,
  }
}
